/**
 * BufferContext — fetches /api/buffer/status + channels once for the
 * planner and shares them with every DraftCard via React Context so we
 * don't refetch on every card mount.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const Ctx = createContext({ status: null, channels: [], byService: {}, ready: false, error: "" });

/* Map a HidroVerse draft channel angle to the Buffer "service" string.
   The planner's target_channel values from the LLM are 'instagram',
   'linkedin-text', 'linkedin-carousel', 'linkedin-video', 'facebook'.
   Buffer reports service in lowercase. */
const ANGLE_TO_SERVICE = {
  instagram: "instagram",
  "linkedin-text": "linkedin",
  "linkedin-carousel": "linkedin",
  "linkedin-video": "linkedin",
  facebook: "facebook",
};

export function channelsForAngle(channels, angle) {
  const svc = ANGLE_TO_SERVICE[(angle || "").trim()];
  if (!svc) return [];
  return channels.filter((c) => (c.service || "").toLowerCase() === svc);
}

export function BufferProvider({ token, children }) {
  const [status, setStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setReady(false);
    setError("");
    try {
      const sR = await fetch(`${API}/api/buffer/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sJ = await sR.json();
      setStatus(sJ);
      if (sJ.connected) {
        const cR = await fetch(`${API}/api/buffer/channels`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const cJ = await cR.json();
        setChannels(cJ.channels || []);
      } else {
        setChannels([]);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setReady(true);
    }
  }, [token]);

  useEffect(() => {
    // Defer to a microtask so the eslint react-hooks/set-state-in-effect
    // rule doesn't trip on the async-chain → setState pattern.
    const t = window.setTimeout(() => { load(); }, 0);
    return () => window.clearTimeout(t);
  }, [load]);

  const byService = channels.reduce((acc, c) => {
    const k = (c.service || "").toLowerCase();
    (acc[k] = acc[k] || []).push(c);
    return acc;
  }, {});

  return (
    <Ctx.Provider value={{ status, channels, byService, ready, error, reload: load }}>
      {children}
    </Ctx.Provider>
  );
}

export function useBuffer() {
  return useContext(Ctx);
}
